import type { InvestorClientAction, Page, StaffClientAction } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  Dialog,
  DialogCancel,
  EmptyState,
  SkeletonText,
  useToast,
} from '@fruuit/ui'
import { ClipboardList, PencilLine, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { apiFetch } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'
import { shortDate } from '../engagements/format'
import { serverErrors } from '../engagements/forms'
import { keys, LANE_D_KEYS } from '../engagements/queries'
import { ClientActionDialog, type CompanyChoice } from './ClientActionDialog'

const FOOTNOTE =
  "Private to your organisation. These records do not change Fruuit's research finding or execute a portfolio instruction."

export interface ClientActionsPanelProps {
  /** Companies the investor can record against (authorized companies, D-05). */
  companies: CompanyChoice[]
  /** Narrow the list to one company or case. */
  issuerId?: string
  engagementId?: string
}

/** The investor's client actions (PORT-013..019): record, edit and delete their own. */
export function ClientActionsPanel({ companies, issuerId, engagementId }: ClientActionsPanelProps) {
  const query: Record<string, string> = {}
  if (issuerId) query.issuer_id = issuerId
  if (engagementId) query.engagement_id = engagementId
  const list = useApiQuery<Page<InvestorClientAction>>(
    [...keys.clientActions, 'investor', query],
    '/client-actions',
    { query: { ...query, limit: 100 } },
  )
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<InvestorClientAction | null>(null)
  const [deleting, setDeleting] = useState<InvestorClientAction | null>(null)

  return (
    <section aria-labelledby="client-actions-heading">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="client-actions-heading" className="text-lg font-semibold text-ink">
            Your actions & decisions
          </h2>
          <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-sage">{FOOTNOTE}</p>
        </div>
        <Button
          size="sm"
          icon={<Plus />}
          onClick={() => setCreating(true)}
          disabled={companies.length === 0}
        >
          Record client action
        </Button>
      </div>
      {list.isPending ? (
        <SkeletonText lines={4} />
      ) : list.isError ? (
        <Callout tone="danger" role="alert">
          {list.error.message}
        </Callout>
      ) : list.data.items.length === 0 ? (
        <EmptyState
          icon={<ClipboardList />}
          title="No client actions have been recorded."
          description="Record a decision to discuss, such as continued monitoring or escalation."
        />
      ) : (
        <ul className="divide-y divide-line-soft rounded-lg border border-line bg-surface">
          {list.data.items.map((a) => (
            <li key={a.id} className="grid gap-3 p-4 sm:grid-cols-[8rem_1fr_auto] sm:p-5">
              <p className="text-[13px] text-sage">{shortDate(a.created_at)}</p>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
                  {a.issuer.name}
                </p>
                <h3 className="mt-0.5 text-[15px] font-semibold text-ink">{a.action}</h3>
                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink-soft">
                  {a.note}
                </p>
                <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-sage">
                  <Badge tone={a.status === 'Discussed' ? 'positive' : 'info'} dot>
                    {a.status}
                  </Badge>
                  <span>{a.mine ? 'Recorded by you' : `Recorded by ${a.owner_name}`}</span>
                  {a.engagement ? <span>· {a.engagement.title}</span> : null}
                </p>
              </div>
              {a.mine ? (
                <div className="flex gap-2 sm:flex-col">
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<PencilLine />}
                    onClick={() => setEditing(a)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 />}
                    onClick={() => setDeleting(a)}
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {creating ? (
        <ClientActionDialog
          open
          mode="create"
          companies={companies}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {editing ? (
        <ClientActionDialog open mode="edit" action={editing} onClose={() => setEditing(null)} />
      ) : null}
      {deleting ? <DeleteDialog action={deleting} onClose={() => setDeleting(null)} /> : null}
    </section>
  )
}

function DeleteDialog({ action, onClose }: { action: InvestorClientAction; onClose: () => void }) {
  const { toast } = useToast()
  const mutation = useApiMutation(
    (input: { revision: number }, { requestId }) =>
      apiFetch(`/client-actions/${action.id}`, { method: 'DELETE', body: input, requestId }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: () => {
        onClose()
        toast({ tone: 'success', message: 'Client action deleted.' })
      },
    },
  )
  const message = mutation.error ? serverErrors(mutation.error).message : null
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Delete this client action?"
      description={`${action.action} · ${action.issuer.name}`}
      footer={
        <>
          <DialogCancel />
          <Button
            variant="danger"
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ revision: action.revision })}
          >
            Delete
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">
        It leaves your list. The audit trail keeps a record that it existed.
      </p>
      {message ? (
        <Callout tone="danger" role="alert" className="mt-3">
          {message}
        </Callout>
      ) : null}
    </Dialog>
  )
}

/** Staff view of client actions: read-only (R-12). */
export function StaffClientActionList({ items }: { items: StaffClientAction[] }) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<ClipboardList />}
        title="No client actions match these filters."
        description="Client actions are recorded by investors for discussion and are read-only here."
      />
    )
  }
  return (
    <ul className="divide-y divide-line-soft rounded-lg border border-line bg-surface">
      {items.map((a) => (
        <li key={a.id} className="grid gap-2 p-4 sm:grid-cols-[8rem_1fr] sm:p-5">
          <p className="text-[13px] text-sage">{shortDate(a.created_at)}</p>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
              {a.org.name} · {a.issuer.name}
            </p>
            <h3 className="mt-0.5 text-[15px] font-semibold text-ink">{a.action}</h3>
            <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink-soft">
              {a.note}
            </p>
            <p className="mt-2 text-xs text-sage">
              {a.status} · {a.owner.name}
              {a.engagement ? ` · ${a.engagement.title}` : ''}
            </p>
          </div>
        </li>
      ))}
    </ul>
  )
}
